import { Button } from "@/components/ui/button";
import { useNavigate } from "react-router-dom";
import { User, GraduationCap, Atom, FlaskConical, BookOpen, Sparkles } from "lucide-react";

const Login = () => {
  const navigate = useNavigate();

  const handleStudentLogin = () => {
    navigate("/dashboard");
  };

  const handleTeacherLogin = () => {
    navigate("/teacher");
  };

  return (
    <div className="min-h-screen bg-[#0f172a] relative overflow-hidden flex items-center justify-center p-6">
      {/* Animated Background */}
      <div className="absolute inset-0 overflow-hidden">
        {/* Gradient Orbs */}
        <div className="absolute top-[-10%] left-[-10%] w-[500px] h-[500px] bg-blue-600/30 rounded-full blur-[120px] animate-pulse" />
        <div className="absolute bottom-[-10%] right-[-10%] w-[500px] h-[500px] bg-purple-600/20 rounded-full blur-[120px] animate-pulse" style={{ animationDelay: '1s' }} />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-cyan-600/10 rounded-full blur-[100px]" />

        {/* Floating Elements */}
        <div className="absolute top-20 left-10 opacity-20 float">
          <Atom className="h-20 w-20 text-blue-400" />
        </div>
        <div className="absolute top-32 right-16 opacity-15 float-delayed">
          <FlaskConical className="h-24 w-24 text-orange-400" />
        </div>
        <div className="absolute bottom-24 left-20 opacity-10 float">
          <BookOpen className="h-16 w-16 text-white" />
        </div>
        <div className="absolute bottom-32 right-24 opacity-15 float-delayed">
          <Sparkles className="h-14 w-14 text-yellow-400" />
        </div>

        {/* Grid Pattern */}
        <div
          className="absolute inset-0 opacity-[0.03]"
          style={{
            backgroundImage: `linear-gradient(rgba(255,255,255,0.1) 1px, transparent 1px),
                              linear-gradient(90deg, rgba(255,255,255,0.1) 1px, transparent 1px)`,
            backgroundSize: '50px 50px'
          }}
        />
      </div>

      {/* Main Card */}
      <div className="w-full max-w-[420px] relative z-10">
        {/* Glass Card */}
        <div className="relative bg-white/5 backdrop-blur-2xl rounded-3xl p-8 border border-white/10 shadow-2xl overflow-hidden">
          {/* Card Glow Effect */}
          <div className="absolute top-0 left-1/2 -translate-x-1/2 w-32 h-32 bg-blue-500/20 rounded-full blur-[60px]" />

          {/* Logo Section */}
          <div className="text-center mb-10 relative">
            {/* Logo Icon */}
            <div className="inline-flex items-center justify-center w-20 h-20 rounded-2xl bg-gradient-to-br from-blue-500 via-blue-600 to-purple-600 mb-6 shadow-lg shadow-blue-500/25 relative">
              <div className="absolute inset-0 bg-gradient-to-br from-white/20 to-transparent rounded-2xl" />
              <GraduationCap className="h-10 w-10 text-white relative z-10" />
            </div>

            <h1 className="text-3xl font-bold text-white mb-2 tracking-tight">
              ChemSmart
            </h1>
            <p className="text-white/50 text-sm font-medium">
              AI-Powered Chemistry Learning
            </p>
          </div>

          {/* Login Buttons */}
          <div className="space-y-3">
            {/* Student Login - Primary */}
            <Button
              onClick={handleStudentLogin}
              className="w-full h-14 text-base font-semibold bg-white text-slate-900 hover:bg-blue-50 hover:scale-[1.02] active:scale-[0.98] transition-all duration-200 rounded-xl shadow-lg shadow-white/10 group"
            >
              <div className="w-8 h-8 rounded-lg bg-blue-100 flex items-center justify-center mr-3 group-hover:bg-blue-200 transition-colors">
                <User className="h-4 w-4 text-blue-600" />
              </div>
              Continue as Student
            </Button>

            {/* Divider */}
            <div className="flex items-center gap-3 py-2">
              <div className="flex-1 h-px bg-white/10" />
              <span className="text-white/30 text-xs font-medium uppercase tracking-wider">or</span>
              <div className="flex-1 h-px bg-white/10" />
            </div>

            {/* Teacher Login - Secondary */}
            <Button
              variant="ghost"
              onClick={handleTeacherLogin}
              className="w-full h-12 text-sm font-medium bg-white/5 border border-white/10 text-white/70 hover:bg-white/10 hover:text-white hover:border-white/20 transition-all duration-200 rounded-xl group"
            >
              <GraduationCap className="h-4 w-4 mr-2 text-white/50 group-hover:text-white/70" />
              Teacher Login
            </Button>
          </div>

          {/* Features */}
          <div className="mt-8 pt-6 border-t border-white/10">
            <div className="flex items-center justify-center gap-6 text-xs text-white/40">
              <span className="flex items-center gap-1.5">
                <div className="w-1.5 h-1.5 rounded-full bg-green-400" />
                Live Classes
              </span>
              <span className="flex items-center gap-1.5">
                <div className="w-1.5 h-1.5 rounded-full bg-blue-400" />
                AI Doubt Solver
              </span>
              <span className="flex items-center gap-1.5">
                <div className="w-1.5 h-1.5 rounded-full bg-purple-400" />
                Smart Attendance
              </span>
            </div>
          </div>
        </div>

        {/* Bottom Text */}
        <p className="text-center text-white/25 text-xs mt-6">
          11th & 12th Grade Chemistry • JEE/NEET Preparation
        </p>
      </div>
    </div>
  );
};

export default Login;
